<template>
	<SlidingSheet
		:is-open="value"
		title="Preferences"
		width="400px"
		@close="handleClose"
	>
		<template #content>
			<!-- Appearance Section -->
			<div class="preference-section">
				<div class="preference-section__header">
					<h4 class="preference-section__title">
						Appearance
					</h4>
					<p class="preference-section__description">
						Choose your preferred theme
					</p>
				</div>

				<div class="preference-section__content">
					<!-- Theme Toggle -->
					<div class="theme-toggle">
						<div class="theme-toggle__icon theme-toggle__icon--sun">
							<v-icon :size="20">
								mdi-weather-sunny
							</v-icon>
						</div>

						<!-- Toggle Switch -->
						<button
							type="button"
							role="switch"
							:aria-checked="isDark"
							:aria-label="`Switch to ${isDark ? 'light' : 'dark'} mode`"
							class="theme-toggle__switch"
							:class="{ 'theme-toggle__switch--active': isDark }"
							@click="toggleTheme"
						>
							<span class="theme-toggle__slider" />
						</button>

						<div class="theme-toggle__icon theme-toggle__icon--moon">
							<v-icon :size="20">
								mdi-weather-night
							</v-icon>
						</div>
					</div>

					<!-- Current Theme Label -->
					<div class="theme-label">
						{{ isDark ? 'Dark' : 'Light' }} mode
					</div>
				</div>
			</div>

			<!-- Chart Colors Section -->
			<div class="preference-section">
				<div class="preference-section__header">
					<h4 class="preference-section__title">
						Chart Colours
					</h4>
					<p class="preference-section__description">
						Customise the colour palette used in dashboard charts
					</p>
				</div>

				<div class="preference-section__content chart-colors-content">
					<div class="colors-list">
						<div
							v-for="(color, index) in editableColors"
							:key="index"
							class="color-entry"
						>
							<div
								class="color-swatch"
								:style="{ backgroundColor: color }"
								@click="openColorPicker(index)"
							/>
							<input
								:ref="`colorPicker${index}`"
								type="color"
								class="hidden-color-picker"
								:value="color"
								@input="onColorPickerChange(index, $event)"
							>
							<input
								type="text"
								:value="editableColors[index]"
								class="hex-input"
								:class="{ 'hex-input-error': !isValidHex(editableColors[index]) }"
								placeholder="#000000"
								@input="onColorInput(index, $event.target.value)"
							>
							<v-btn
								icon
								x-small
								:disabled="editableColors.length <= minColors"
								class="delete-btn"
								@click="removeColor(index)"
							>
								<v-icon small>
									mdi-delete-outline
								</v-icon>
							</v-btn>
						</div>
					</div>

					<v-btn
						outlined
						block
						class="add-color-btn text-none"
						:disabled="editableColors.length >= maxColors"
						@click="addColor"
					>
						<v-icon left>
							mdi-plus
						</v-icon>
						Add Colour
					</v-btn>

					<div
						v-if="colorValidationError"
						class="validation-error"
					>
						{{ colorValidationError }}
					</div>

					<div class="color-actions">
						<v-btn
							text
							small
							class="text-none"
							@click="resetColorsToDefaults"
						>
							<v-icon
								small
								left
							>
								mdi-refresh
							</v-icon>
							Reset
						</v-btn>
						<v-btn
							color="primary"
							small
							class="text-none"
							:disabled="!isColorsValid"
							@click="saveColors"
						>
							Save
						</v-btn>
					</div>
				</div>
			</div>
		</template>
	</SlidingSheet>
</template>

<script>
import SlidingSheet from './sliding-sheet.vue'
import { clearCustomColorsCache } from '../utils/getChartColorByIndex.js'
import { notifyChartColorsChanged } from '../state/chart-colors.js'
import { applyDarkMode, loadDarkModePreference, saveDarkModePreference } from '../utils/dark-mode.js'
const COLORS_STORAGE_KEY = 'chartColors'
const DEFAULT_COLORS = [
	'#0088FE',
	'#00C49F',
	'#FFBB28',
	'#FF8042',
	'#8884D8',
	'#82CA9D',
	'#FFC658',
	'#8DD1E1'
]
const MIN_COLORS = 8
const MAX_COLORS = 12

export default {
	name: 'preferences-sheet',

	components: {
		SlidingSheet
	},

	props: {
		value: {
			type: Boolean,
			required: true
		}
	},

	data () {
		return {
			isDark: false,
			editableColors: [...DEFAULT_COLORS],
			minColors: MIN_COLORS,
			maxColors: MAX_COLORS
		}
	},

	computed: {
		colorValidationError () {
			if (this.editableColors.length < this.minColors) {
				return `Minimum ${this.minColors} colours required`
			}
			if (this.editableColors.length > this.maxColors) {
				return `Maximum ${this.maxColors} colours allowed`
			}
			const invalidColors = this.editableColors.filter(c => !this.isValidHex(c))
			if (invalidColors.length > 0) {
				return 'All colours must be valid HEX format'
			}
			return null
		},

		isColorsValid () {
			return !this.colorValidationError
		}
	},

	watch: {
		value (newVal) {
			if (newVal) {
				this.loadColors()
			}
		}
	},

	mounted () {
		this.loadThemePreference()
		this.loadColors()
	},

	methods: {
		handleClose () {
			this.$emit('input', false)
		},

		loadThemePreference () {
			const saved = loadDarkModePreference()
			this.isDark = saved === true
		},

		toggleTheme () {
			this.isDark = !this.isDark
			applyDarkMode(this.$vuetify, this.isDark)
			saveDarkModePreference(this.isDark)
			this.$emit('theme-change', this.isDark ? 'dark' : 'light')
		},

		// Chart Colors Methods
		isValidHex (color) {
			if (!color) return false
			const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
			return hexRegex.test(color)
		},

		openColorPicker (index) {
			const picker = this.$refs[`colorPicker${index}`]
			if (picker) {
				const input = Array.isArray(picker) ? picker[0] : picker
				if (input) {
					input.click()
				}
			}
		},

		onColorPickerChange (index, event) {
			const newColor = event.target.value.toUpperCase()
			this.$set(this.editableColors, index, newColor)
		},

		onColorInput (index, value) {
			if (!value) return

			// Compute formatted value (uppercase, with # prefix)
			const formattedValue = value.startsWith('#')
				? value.toUpperCase()
				: '#' + value.toUpperCase()

			// Use Vue's reactive setter for proper reactivity in Vue 2
			this.$set(this.editableColors, index, formattedValue)
		},

		loadColors () {
			try {
				const stored = localStorage.getItem(COLORS_STORAGE_KEY)
				if (stored) {
					const parsed = JSON.parse(stored)
					if (Array.isArray(parsed) && parsed.length >= this.minColors) {
						this.editableColors = [...parsed]
						return
					}
				}
			} catch (e) {
				console.warn('Failed to load chart colors from localStorage:', e)
			}
			this.editableColors = [...DEFAULT_COLORS]
		},

		addColor () {
			if (this.editableColors.length < this.maxColors) {
				this.editableColors.push('#808080')
			}
		},

		removeColor (index) {
			if (this.editableColors.length > this.minColors) {
				this.editableColors.splice(index, 1)
			}
		},

		resetColorsToDefaults () {
			this.editableColors = [...DEFAULT_COLORS]
			this.saveColors()
		},

		saveColors () {
			if (!this.isColorsValid) return

			try {
				localStorage.setItem(COLORS_STORAGE_KEY, JSON.stringify(this.editableColors))
				clearCustomColorsCache()
				notifyChartColorsChanged()
				this.$emit('colors-updated', this.editableColors)
			} catch (e) {
				console.error('Failed to save chart colors:', e)
			}
		}
	}
}
</script>

<style scoped lang="scss">
.preference-section {
	margin-bottom: 24px;

	&__header {
		margin-bottom: 16px;
	}

	&__title {
		margin: 0 0 4px 0;
		font-size: 14px;
		font-weight: 600;
		color: var(--global-foreground, #1f2937);
	}

	&__description {
		margin: 0;
		font-size: 13px;
		color: var(--global-muted-foreground, #6b7280);
	}

	&__content {
		padding: 16px;
		background: var(--global-main-background-color, #f9fafb);
		border-radius: var(--radius, 8px);
	}
}

.theme-toggle {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 16px;
	margin-bottom: 12px;

	&__icon {
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--global-muted-foreground, #9ca3af);
		transition: color 0.2s ease;

		&--sun {
			color: #f59e0b;
		}

		&--moon {
			color: #6366f1;
		}
	}

	&__switch {
		position: relative;
		width: 52px;
		height: 28px;
		background: #d1d5db;
		border: none;
		border-radius: 14px;
		cursor: pointer;
		transition: background-color 0.3s ease;
		padding: 0;

		&:hover {
			background: #9ca3af;
		}

		&:focus-visible {
			outline: 2px solid var(--global-primary, #406A94);
			outline-offset: 2px;
		}

		&--active {
			background: var(--global-syrenis-red, #ed303e);

			&:hover {
				background: #d42a37;
			}

			.theme-toggle__slider {
				transform: translateX(24px);
			}
		}
	}

	&__slider {
		position: absolute;
		top: 2px;
		left: 2px;
		width: 24px;
		height: 24px;
		background: white;
		border-radius: 50%;
		transition: transform 0.3s ease;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
	}
}

.theme-label {
	text-align: center;
	font-size: 13px;
	font-weight: 500;
	color: var(--global-foreground, #374151);
}

// Chart Colors Section
.chart-colors-content {
	padding: 12px !important;
}

.colors-list {
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-bottom: 8px;
}

.color-entry {
	display: flex;
	align-items: center;
	gap: 6px;
	position: relative;
}

.color-swatch {
	width: 28px;
	height: 28px;
	border-radius: 4px;
	border: 1px solid var(--global-border-color, #e2e8f0);
	flex-shrink: 0;
	cursor: pointer;
	transition: transform 0.15s ease, box-shadow 0.15s ease;

	&:hover {
		transform: scale(1.08);
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
	}
}

.hidden-color-picker {
	position: absolute;
	width: 0;
	height: 0;
	opacity: 0;
	pointer-events: none;
}

.hex-input {
	flex: 1;
	height: 28px;
	padding: 0 8px;
	border: 1px solid var(--global-border-color, #e2e8f0);
	border-radius: 4px;
	font-family: inherit;
	font-size: 13px;
	color: var(--global-foreground, #374151);
	background: var(--global-card-background, #fff);
	outline: none;
	transition: border-color 0.15s ease;

	&:focus {
		border-color: var(--global-primary, #406A94);
	}
}

.hex-input-error {
	border-color: var(--global-color-red, #ef4444) !important;
}

.delete-btn {
	color: var(--global-muted-foreground, #64748b);

	&:hover:not(:disabled) {
		color: var(--global-color-red, #ef4444);
	}
}

.add-color-btn {
	margin-top: 4px;
	border-style: dashed;
	height: 32px !important;
	min-height: 32px !important;
	font-size: 13px;
}

.validation-error {
	margin-top: 8px;
	padding: 8px 10px;
	background-color: rgba(239, 68, 68, 0.1);
	border-radius: 4px;
	color: var(--global-color-red, #ef4444);
	font-size: 12px;
}

.color-actions {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 12px;
	padding-top: 12px;
	border-top: 1px solid var(--global-border-color, #e5e7eb);
}

// Dark mode support for chart colors
.dark {
	.color-swatch {
		border-color: var(--global-border-color, #374151);
	}

	.hex-input {
		background: var(--global-card-background, #1c1c1e);
		border-color: var(--global-border-color, #374151);
		color: var(--global-foreground, #f5f5f5);
	}

	.color-actions {
		border-color: var(--global-border-color, #374151);
	}
}
</style>
